import React, { useState, useRef, useEffect } from "react";
import { MapPin } from "lucide-react";

interface AddressAutocompleteProps {
  onAddressSelect: (address: string, coordinates: [number, number], displayName?: string, openingHours?: any) => void;
  initialValue?: string;
  placeholder?: string;
  fields: string[];
}

declare global {
  interface Window {
    google: any;
  }
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ onAddressSelect, initialValue = "", placeholder = "Type an address...", fields }) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<number>();
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Update input value when initialValue changes
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    // No need to initialize services for the new API
    // AutocompleteSuggestion is used directly when needed
  }, []);

  const searchPlaces = async (query: string) => {
    if (!window.google?.maps?.places || query.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);

    try {
      // Create session token on first keystroke of a new search session
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      }

      const request = {
        input: query,
        includedRegionCodes: ["dk"], // Restrict to Denmark
        includedPrimaryTypes: ["establishment", "street_address", "route", "locality"],
        sessionToken: sessionTokenRef.current,
      };

      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (import.meta.env.DEV) {
        console.log("Autocomplete suggestions:", suggestions?.length || 0, "results");
      }

      setIsLoading(false);

      if (suggestions && suggestions.length > 0) {
        setPredictions(suggestions.slice(0, 5)); // Limit to 5 suggestions
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
      setIsLoading(false);
      setPredictions([]);
      setShowDropdown(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Debounce the search to avoid too many API calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchPlaces(value);
    }, 300);
  };

  const handlePlaceSelect = async (placeId: string, description: string) => {
    if (import.meta.env.DEV) {
      console.log("Place selected:", placeId, description);
    }

    if (!window.google?.maps?.places) {
      console.log("Google Maps Places not available");
      return;
    }

    setIsLoading(true);
    setInputValue(description);
    setShowDropdown(false);

    try {
      // Use the new Place class with the correct syntax
      const { Place } = window.google.maps.places;

      const place = new Place({
        id: placeId,
        requestedLanguage: "da", // Danish language
      });

      console.log("Making place details request with new API...");

      // Fetch the place details using the new API with correct field names
      // Pass session token to bundle autocomplete + details into single billing event
      await place.fetchFields({
        fields,
        sessionToken: sessionTokenRef.current,
      });

      console.log("=== PLACE DETAILS RESPONSE ===");
      console.log("Place object:", place);
      console.log("Location:", place.location);
      console.log("Phone:", place.nationalPhoneNumber);
      console.log("Website:", place.websiteURI);
      console.log("Rating:", place.rating);
      console.log("User rating count:", place.userRatingCount);
      console.log("Business status:", place.businessStatus);
      console.log("Types:", place.types);

      console.log("=== OPENING HOURS DETAILED INFO ===");
      console.log("Regular opening hours object:", place.regularOpeningHours);

      if (place.regularOpeningHours) {
        console.log("📅 Opening Hours Available!");
        console.log("Weekday text array:", place.regularOpeningHours.weekdayText);

        if (place.regularOpeningHours.weekdayText) {
          console.log("📋 Human-readable opening hours:");
          place.regularOpeningHours.weekdayText.forEach((dayText: string, index: number) => {
            const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            console.log(`  ${days[index]}: ${dayText}`);
          });
        }

        console.log("Periods array:", place.regularOpeningHours.periods);
        if (place.regularOpeningHours.periods) {
          console.log("📊 Structured opening hours periods:");
          place.regularOpeningHours.periods.forEach((period: any, index: number) => {
            console.log(`  Period ${index + 1}:`, {
              open: period.open,
              close: period.close,
            });
            if (period.open) {
              console.log(`    Opens: Day ${period.open.day} at ${period.open.hour}:${period.open.minute?.toString().padStart(2, "0") || "00"}`);
            }
            if (period.close) {
              console.log(`    Closes: Day ${period.close.day} at ${period.close.hour}:${period.close.minute?.toString().padStart(2, "0") || "00"}`);
            }
          });
        }
      } else {
        console.log("❌ No opening hours available for this place");
      }

      setIsLoading(false);

      if (place.location) {
        const lat = place.location.lat();
        const lng = place.location.lng();
        const address = place.formattedAddress || description;
        const displayName = place.displayName || "";

        console.log("Extracted coordinates:", { lat, lng });
        console.log("Final address:", address);
        console.log("Display name:", displayName);

        setInputValue(address);
        onAddressSelect(address, [lat, lng], displayName, place.regularOpeningHours);
        // Reset session token after successful selection
        sessionTokenRef.current = null;
      } else {
        console.log("No location found in place details");
        // Fallback to just using the description
        setInputValue(description);
        onAddressSelect(description, [55.4038, 10.4024]); // Default Denmark coordinates
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
      setIsLoading(false);
      // Fallback to just using the description
      setInputValue(description);
      onAddressSelect(description, [55.4038, 10.4024]); // Default Denmark coordinates
      // Reset session token even on error
      sessionTokenRef.current = null;
    }
  };

  const handleBlur = () => {
    // Delay hiding dropdown to allow click events
    setTimeout(() => setShowDropdown(false), 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 3 && predictions.length > 0 && setShowDropdown(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        />
        <MapPin size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {predictions.map((suggestion, index) => (
            <button
              key={suggestion.placePrediction?.placeId || index}
              type="button"
              onClick={() => handlePlaceSelect(suggestion.placePrediction?.placeId || "", suggestion.placePrediction?.text?.text || "")}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 last:border-b-0 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700"
            >
              <div className="font-medium text-gray-900 dark:text-white text-sm">{suggestion.placePrediction?.structuredFormat?.mainText?.text || suggestion.placePrediction?.text?.text}</div>
              {suggestion.placePrediction?.structuredFormat?.secondaryText?.text && (
                <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{suggestion.placePrediction?.structuredFormat?.secondaryText?.text}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
